import { FC, useState } from 'react';
import { AssistantMemoryResponse, AssistantSavedMemory } from '@/types/assistant';

interface Props {
  memory: AssistantMemoryResponse | null;
  loading?: boolean;
  onAdd: (content: string) => Promise<void> | void;
  onEdit: (id: string, content: string) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
}

const AssistantMemoryPanel: FC<Props> = ({ memory, loading, onAdd, onEdit, onDelete }) => {
  const [newFact, setNewFact] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [adding, setAdding] = useState(false);

  const saved = (memory?.saved ?? []).filter((m) => m.is_active);
  const autoFacts = memory?.auto?.facts ?? [];
  const autoSummary = memory?.auto?.summary?.trim() ?? '';

  async function submitNew() {
    const content = newFact.trim();
    if (!content || adding) return;
    setAdding(true);
    try {
      await onAdd(content);
      setNewFact('');
    } finally {
      setAdding(false);
    }
  }

  function startEdit(m: AssistantSavedMemory) {
    setEditingId(m.id);
    setEditDraft(m.content);
  }

  async function commitEdit() {
    if (editingId) {
      const content = editDraft.trim();
      if (content) await onEdit(editingId, content);
    }
    setEditingId(null);
    setEditDraft('');
  }

  return (
    <div data-testid="assistant-memory-panel" className="p-4 space-y-5">
      <div>
        <h3 className="text-sm font-bold text-content-primary flex items-center gap-2">
          <span className="material-symbols-outlined text-accent text-lg">neurology</span>
          O que o H.Bot lembra de você
        </h3>
        <p className="text-xs text-content-tertiary mt-1">
          O H.Bot lembra disso pra te ajudar melhor. Você manda aqui — adicione, edite ou apague.
        </p>
      </div>

      {loading && <p className="text-xs text-content-tertiary">Carregando memória…</p>}

      {/* Fatos salvos pelo usuário (editáveis) */}
      <div className="space-y-2">
        <p className="text-2xs uppercase tracking-wider text-content-tertiary font-semibold">
          Seus fatos
        </p>
        {saved.length === 0 && !loading && (
          <p className="text-xs text-content-tertiary">
            Nada salvo ainda. Conta algo que você quer que o H.Bot lembre.
          </p>
        )}
        {saved.map((m) => (
          <div
            key={m.id}
            data-testid="saved-memory"
            className="group rounded-lg border border-surface-border bg-surface-secondary p-2.5"
          >
            {editingId === m.id ? (
              <div className="space-y-2">
                <textarea
                  data-testid="memory-edit-input"
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  rows={2}
                  className="w-full resize-none rounded-md bg-surface-primary border border-accent/40 px-2 py-1.5 text-sm text-content-primary focus:outline-none"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="text-xs text-content-tertiary hover:text-content-primary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    data-testid="memory-save-edit"
                    onClick={commitEdit}
                    className="text-xs font-semibold text-accent"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <p className="flex-1 text-sm text-content-secondary">{m.content}</p>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition shrink-0">
                  <button
                    type="button"
                    data-testid="memory-edit"
                    onClick={() => startEdit(m)}
                    aria-label="Editar fato"
                    className="p-1 rounded hover:bg-surface-hover text-content-tertiary hover:text-content-primary"
                  >
                    <span className="material-symbols-outlined text-base leading-none">edit</span>
                  </button>
                  <button
                    type="button"
                    data-testid="memory-delete"
                    onClick={() => onDelete(m.id)}
                    aria-label="Apagar fato"
                    className="p-1 rounded hover:bg-surface-hover text-content-tertiary hover:text-red-400"
                  >
                    <span className="material-symbols-outlined text-base leading-none">delete</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="flex gap-2">
          <input
            data-testid="memory-add-input"
            value={newFact}
            onChange={(e) => setNewFact(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submitNew();
              }
            }}
            placeholder="Ex: Trabalho no setor de Compras."
            className="flex-1 rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-sm text-content-primary placeholder:text-content-tertiary focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            data-testid="memory-add"
            onClick={submitNew}
            disabled={!newFact.trim() || adding}
            className="px-3 rounded-lg bg-accent text-surface-primary font-semibold text-sm disabled:opacity-40"
          >
            Adicionar
          </button>
        </div>
      </div>

      {/* Resumo automático (read-only) */}
      {(autoSummary || autoFacts.length > 0) && (
        <div className="space-y-2 border-t border-surface-border pt-4">
          <p className="text-2xs uppercase tracking-wider text-content-tertiary font-semibold">
            Aprendido automaticamente
          </p>
          {autoSummary && <p className="text-xs text-content-secondary">{autoSummary}</p>}
          {autoFacts.length > 0 && (
            <ul className="list-disc list-inside space-y-1">
              {autoFacts.map((f, i) => (
                <li key={i} className="text-xs text-content-tertiary">
                  {f}
                </li>
              ))}
            </ul>
          )}
          <p className="text-2xs text-content-tertiary italic">
            Isso o H.Bot percebeu sozinho conversando com você.
          </p>
        </div>
      )}
    </div>
  );
};

export default AssistantMemoryPanel;
